import { useParams, Link, useNavigate } from "react-router-dom";
import { Layout } from "@jambushrushti/components/layout/Layout";
import { PageBreadcrumb } from "@jambushrushti/components/ui/page-breadcrumb";
import { SimilarItems } from "@jambushrushti/components/ui/similar-items";
import { useArticlesFromDB } from "@jambushrushti/hooks/useContent";
import { Button } from "@jambushrushti/components/ui/button";
import { Badge } from "@jambushrushti/components/ui/badge";
import { SocialShareButtons } from "@jambushrushti/components/social/SocialShareButtons";
import {
  ArrowLeft,
  Download,
  ExternalLink,
  FileText,
  User,
  Globe,
  Tag,
  Calendar,
  BookOpen,
  Loader2,
} from "lucide-react";

const ArticleDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: articles = [], isLoading } = useArticlesFromDB();

  const article = articles.find(a => a.id === id);

  // Get similar articles (same category, excluding current)
  const similarArticles = article
    ? articles
        .filter(a => a.category === article.category && a.id !== article.id)
        .slice(0, 4)
    : [];

  if (isLoading) {
    return (
      <Layout>
        <section className="bg-background py-20">
          <div className="container mx-auto px-4 text-center">
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-primary" />
            <p className="mt-4 text-muted-foreground">Loading article...</p>
          </div>
        </section>
      </Layout>
    );
  }

  if (!article) {
    return (
      <Layout>
        <section className="bg-background py-20">
          <div className="container mx-auto px-4 text-center">
            <FileText className="mx-auto mb-4 h-16 w-16 text-muted-foreground/30" />
            <h1 className="font-heading mb-4 text-2xl font-bold text-foreground">
              Article Not Found
            </h1>
            <p className="mb-6 text-muted-foreground">
              The article you're looking for doesn't exist or has been removed.
            </p>
            <Button variant="hero" asChild>
              <Link to="/articles">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Articles
              </Link>
            </Button>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Breadcrumb */}
      <PageBreadcrumb
        items={[
          { label: "Articles", href: "/articles" },
          { label: article.title },
        ]}
      />

      {/* Article Details */}
      <section className="bg-background py-12 lg:py-16">
        <div className="container mx-auto px-4">
          <div className="grid items-start gap-12 lg:grid-cols-2">
            {/* Article Image */}
            <div className="group relative">
              <div className="shadow-elegant mx-auto aspect-[4/3] max-w-lg overflow-hidden rounded-2xl bg-secondary/30 lg:mx-0">
                <img
                  src={
                    article.cover_image ||
                    "https://siddhijambuparivar.com/wp-content/uploads/2022/11/96-min.jpg"
                  }
                  alt={article.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </div>

            {/* Article Info */}
            <div className="animate-fade-up space-y-6">
              <div>
                <div className="mb-4 flex items-center gap-2">
                  {article.category && (
                    <Badge variant="default">{article.category}</Badge>
                  )}
                </div>

                <h1 className="font-heading mb-4 text-3xl font-bold text-foreground lg:text-4xl">
                  {article.title}
                </h1>

                <div className="flex items-center gap-3 text-muted-foreground">
                  <User className="h-4 w-4 text-primary" />
                  <span className="font-body text-sm">
                    <span className="font-medium text-foreground">Author:</span>{" "}
                    {article.author || "Author not specified"}
                  </span>
                </div>
              </div>

              {/* Download Button */}
              <Button variant="hero" size="lg" className="w-full sm:w-auto">
                <Download className="mr-2 h-4 w-4" />
                Download PDF
              </Button>

              {/* Meta Info */}
              <div className="flex flex-wrap gap-4 border-t border-border pt-4">
                {article.category && (
                  <div className="flex items-center gap-2">
                    <Tag className="h-4 w-4 text-muted-foreground" />
                    <span className="font-body text-sm text-muted-foreground">
                      Category:
                    </span>
                    <Badge variant="secondary">{article.category}</Badge>
                  </div>
                )}

                {/* Share Buttons */}
                <div className="mt-4 border-t border-border pt-4">
                  <SocialShareButtons
                    title={article.title}
                    description={article.excerpt || ""}
                    compact
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="bg-secondary/20 py-10">
        <div className="container mx-auto px-4">
          <h2 className="font-heading mb-4 text-xl font-bold text-primary">
            About this Article
          </h2>
          {article.excerpt && (
            <p className="font-body max-w-3xl text-base leading-relaxed text-muted-foreground">
              {article.excerpt}
            </p>
          )}
          {article.content && (
            <div className="font-body mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground">
              {article.content.split("\n\n").map((paragraph, index) => (
                <p key={index} className="mb-4">
                  {paragraph}
                </p>
              ))}
            </div>
          )}
          {!article.content && !article.excerpt && (
            <p className="font-body max-w-3xl text-base leading-relaxed text-muted-foreground">
              Content is not available yet.
            </p>
          )}
        </div>
      </section>

      {/* Similar Articles */}
      {similarArticles.length > 0 && (
        <SimilarItems
          title="Similar Articles"
          subtitle="Read More"
          items={similarArticles.map(a => ({
            id: a.id,
            title: a.title,
            image:
              a.cover_image ||
              "https://siddhijambuparivar.com/wp-content/uploads/2022/11/96-min.jpg",
            category: a.category || "Article",
            subtitle: a.author || "Author not specified",
          }))}
          basePath="/articles"
        />
      )}

      {/* CTA Section */}
      <section className="border-t border-border bg-background py-12">
        <div className="container mx-auto px-4 text-center">
          <p className="font-body mb-4 text-muted-foreground">
            Explore more scholarly articles and tributes
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button variant="hero" asChild>
              <Link to="/articles">
                <BookOpen className="mr-2 h-4 w-4" />
                Browse All Articles
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/books">View Books</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ArticleDetails;
